import { getTopic, getTopics } from 'src/apis/helpdesk/topic.api';
import {
  FilePathAdd,
  ITopic,
  ITopicDetail,
  ITopicsParams,
  RoomChatState,
  TopicDetailAddType
} from 'src/types/helpdesk/topic.type';
import {
  clearAllChatDB,
  getAllFilesFromDB,
  getAllTopicIdsFromDB,
  getMessagesFromDB,
  // ✅ Add these missing imports:
  saveFileWithMetadata,
  saveMessagesToDB
} from 'src/utils/chatFileDB';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface FileData {
  url: string;
  metadata: {
    roomId?: string;
    fileId?: string;
    originalName: string;
    fileType: string;
    fileSize: number;
    thumbnail?: string; // ✅ Add thumbnail URL
    fullImage?: string; // ✅ Add full image URL
    isLoaded?: boolean; // ✅ Track if full image is loaded
  };
  lastAccessed: number;
}

const defaultRoomState: Partial<RoomChatState> = {
  allMessages: [],
  visibleMessages: [],
  paging: { page: 1, pageSize: 50, total: 0, hasMore: true },
  loading: false,
  isAtBottom: true,
  pendingMessages: [],
  focusMessageId: undefined,
  scrollPosition: undefined
};

interface ChatFileState {
  // #region States
  messageCount: number; // Số lượng tin nhắn được prepend/append ở mỗi lần load
  maxWindow: number; // Số lượng tin nhắn tối đa trong window

  rooms: Record<string, RoomChatState>; // Fresh từ API
  files: Record<string, FileData>;

  topicParams: ITopicsParams; // Fresh từ API
  topicsPagination: {
    currentPage: number;
    totalPages: number;
    hasMore: boolean;
  };
  newMessage: TopicDetailAddType;

  // Session Management
  _hasHydrated: boolean; // Track hydration state
  isInitialized: boolean; // Track session initialization
  // #endregion

  // #region Actions
  // Common
  resetStore: () => void;

  // Message File
  setFile: (
    key: string,
    url: string,
    metadata?: {
      fileId?: string;
      fileName?: string;
      fileType?: string;
      fileSize?: number;
      roomId?: string;
      thumbnail?: string;
      fullImage?: string;
    }
  ) => void;
  setFileWithThumbnail: (
    key: string,
    thumbnailUrl: string,
    fullImageUrl: string,
    metadata?: {
      fileId?: string;
      fileName?: string;
      fileType?: string;
      fileSize?: number;
      roomId?: string;
    }
  ) => void;
  loadFullImage: (key: string) => void;
  getImageUrl: (key: string, preferFullImage?: boolean) => string | null;
  removeFile: (key: string) => void;
  markAccessed: (key: string) => void;
  clearAll: () => void;

  // Messages
  setMessages: (
    topicId: string,
    messagesOrUpdater: ITopicDetail[] | ((prev: ITopicDetail[]) => ITopicDetail[])
  ) => void;
  updateMessage: (topicId: string, messageId: string, message: Partial<ITopicDetail>) => void;
  prependMessages: (topicId: string) => void;
  appendMessages: (topicId: string, count?: number) => void;
  setVisibleById: (topicId: string, id: string) => void;
  addPendingMessage: (topicId: string, msg: ITopicDetail) => void;
  updatePendingMessage: (topicId: string, localId: string, msg: ITopicDetail) => void;
  removePendingMessage: (topicId: string, localId: string) => void;
  focusToMessage: (topicId: string, id?: string) => void;
  setIsAtBottom: (topicId: string, isAtBottom: boolean) => void;
  setLoading: (topicId: string, val: boolean) => void;
  setScrollPosition: (topicId: string, position: number) => void;
  // Chat Input
  updateNewMessage: (message: TopicDetailAddType) => void;
  removeFileInNewMessage: (uid: string) => void;
  addFileInNewMessage: (file: FilePathAdd) => void;
  resetNewMessage: () => void;

  // Rooms
  setRooms: (rooms: ITopic[]) => void;
  addRooms: (rooms: any[]) => void;
  setRoomInfo: (roomId: string, roomInfo: any) => void;
  updateRoomPartial: (roomId: string, partial: Partial<any>) => void;
  loadMoreTopics: () => Promise<void>;
  updateUnReadMessages: (roomId: string, unreadCount: number) => void;

  // Session management methods
  loadRoomInfo: (roomId: string) => Promise<void>;
  loadPinnedMessages: (roomId: string) => Promise<void>;
  initializeSession: (topicParams?: ITopicsParams) => Promise<void>;
  restoreFromIndexedDB: () => Promise<void>;
  setPinTopicDetails: (details: any[]) => void;

  // Topic Params
  updateTopicParams: (updater: (prev: ITopicsParams) => ITopicsParams) => void;
  resetTopicParams: () => void;

  // #endregion
}

export const useChatStore = create<ChatFileState>()(
  devtools(
    (set, get) => ({
      // #region States
      messageCount: 10,
      maxWindow: 30,
      files: {}, // PERSISTED
      rooms: {}, // MEMORY ONLY
      newMessage: { content: '' },
      pinTopicDetails: [], // MEMORY ONLY
      _hasHydrated: false, // MEMORY ONLY
      isInitialized: false, // MEMORY ONLY
      topicsPagination: {
        currentPage: 1,
        totalPages: 1,
        hasMore: true
      },
      topicParams: {
        page: 1,
        pageSize: 50,
        isActive: true
      },
      // #endregion

      // #region Actions
      // #region Actions - Common
      resetStore: () => {
        set((state) => {
          // Clear URL.createObjectURL nếu có
          Object.values(state.files).forEach((f) => URL.revokeObjectURL(f.url));
          return {
            files: {},
            rooms: {},
            pinTopicDetails: [],
            isInitialized: false
          };
        });
      },
      // #endregion

      // #region Actions - Message File
      // Mark a file as accessed
      markAccessed: (key) =>
        set((state) => {
          if (!state.files[key]) return state;
          return {
            files: {
              ...state.files,
              [key]: { ...state.files[key], lastAccessed: Date.now() }
            }
          };
        }),
      setFile: (key, url, metadata = {}) =>
        set((state) => {
          const newFiles = {
            ...state.files,
            [key]: {
              url,
              lastAccessed: Date.now(),
              metadata: {
                fileId: metadata.fileId || '',
                originalName: metadata.fileName || 'unknown',
                fileType: metadata.fileType || 'application/octet-stream',
                fileSize: metadata.fileSize || 0,
                roomId: metadata.roomId || '',
                thumbnail: metadata.thumbnail,
                fullImage: metadata.fullImage,
                isLoaded: false
              }
            } as FileData
          };

          // ✅ Auto-save to IndexedDB when blob URL + required metadata
          if (url.startsWith('blob:') && metadata.roomId && metadata.fileId) {
            fetch(url)
              .then((response) => response.blob())
              .then((blob) => {
                return saveFileWithMetadata(
                  metadata.roomId!, // ✅ Non-null assertion since we checked above
                  metadata.fileId!,
                  blob,
                  {
                    fileName: metadata.fileName || 'unknown',
                    fileType: metadata.fileType || blob.type || 'application/octet-stream',
                    fileSize: metadata.fileSize || blob.size
                  }
                );
              })
              .catch((error) => {
                console.error('❌ Failed to save file to IndexedDB:', key, error);
              });
          }

          return { files: newFiles };
        }),
      setFileWithThumbnail: (key, thumbnailUrl, fullImageUrl, metadata = {}) =>
        set((state) => {
          const newFiles = {
            ...state.files,
            [key]: {
              url: thumbnailUrl, // Start with thumbnail
              lastAccessed: Date.now(),
              metadata: {
                fileId: metadata.fileId || '',
                originalName: metadata.fileName || 'unknown',
                fileType: metadata.fileType || 'image/jpeg',
                fileSize: metadata.fileSize || 0,
                roomId: metadata.roomId || '',
                thumbnail: thumbnailUrl,
                fullImage: fullImageUrl,
                isLoaded: false // Start with thumbnail
              }
            } as FileData
          };

          // ✅ Auto-save thumbnail to IndexedDB
          if (thumbnailUrl.startsWith('blob:') && metadata.roomId && metadata.fileId) {
            fetch(thumbnailUrl)
              .then((response) => response.blob())
              .then((blob) => {
                return saveFileWithMetadata(
                  metadata.roomId!,
                  `${metadata.fileId}_thumb`, // Different key for thumbnail
                  blob,
                  {
                    fileName: `thumb_${metadata.fileName || 'unknown'}`,
                    fileType: metadata.fileType || blob.type,
                    fileSize: metadata.fileSize || blob.size
                  }
                );
              })
              .catch((error) => {
                console.error('❌ Failed to save thumbnail:', key, error);
              });
          }

          return { files: newFiles };
        }),
      loadFullImage: (key) =>
        set((state) => {
          const file = state.files[key];
          if (!file?.metadata?.fullImage || file.metadata.isLoaded) {
            return state;
          }

          console.log('🖼️ Loading full image for:', key);

          return {
            files: {
              ...state.files,
              [key]: {
                ...file,
                url: file.metadata.fullImage, // Switch to full image
                metadata: {
                  ...file.metadata,
                  isLoaded: true
                },
                lastAccessed: Date.now()
              }
            }
          };
        }),

      getImageUrl: (key, preferFullImage = false) => {
        const state = get();
        const file = state.files[key];
        if (!file) return null;

        // ✅ Return appropriate URL based on preference
        if (preferFullImage && file.metadata?.fullImage) {
          return file.metadata.fullImage;
        }

        return file.metadata?.thumbnail || file.url;
      },
      removeFile: (key) =>
        set((state) => {
          const { [key]: removed, ...rest } = state.files;
          if (removed) URL.revokeObjectURL(removed.url);
          return { files: rest };
        }),
      clearAll: () =>
        set((state) => {
          // Revoke blob URLs
          Object.values(state.files).forEach((f) => URL.revokeObjectURL(f.url));

          // Clear IndexedDB
          clearAllChatDB().catch(console.error);

          return { files: {}, messages: {} };
        }),
      // #endregion

      // #region Actions - Messages
      setMessages: (topicId, messagesOrUpdater) => {
        set((state) => {
          const currentMessages = state.rooms[topicId].allMessages || [];
          const newMessages =
            typeof messagesOrUpdater === 'function' ? messagesOrUpdater(currentMessages) : messagesOrUpdater;

          // ✅ Auto-save to IndexedDB immediately
          if (newMessages && newMessages.length > 0) {
            saveMessagesToDB(topicId, newMessages).catch(console.error);
          }

          return {
            rooms: {
              ...state.rooms,
              [topicId]: {
                ...defaultRoomState,
                ...state.rooms[topicId],
                allMessages: newMessages
              }
            }
          };
        });
      },
      updateMessage: (topicId: string, tempId: string, update: Partial<ITopicDetail>) => {
        set((state) => {
          const prevRoom = state.rooms[topicId] || defaultRoomState;
          const newMessages = prevRoom.allMessages.map((msg) => (msg.tempId === tempId ? { ...msg, ...update } : msg));
          // Optional: save lại vào DB
          return {
            rooms: {
              ...state.rooms,
              [topicId]: {
                ...prevRoom,
                allMessages: newMessages
              }
            }
          };
        });
      },
      prependMessages: (topicId: string) =>
        set((state) => {
          const prev = state.rooms[topicId] || defaultRoomState;
          const all = prev.allMessages;
          const messageCount = state.messageCount;
          const maxWindow = state.maxWindow;

          // Lấy id message đầu tiên hiện tại
          const firstId = prev.visibleMessages?.[0]?.id;
          const idx = all.findIndex((m) => m.id === firstId);

          if (idx === -1) return state; // Nếu ko tìm thấy thì không làm gì cả

          // Tính chỉ số bắt đầu mới (prepend thêm messageCount vào đầu)
          let start = Math.max(0, idx - messageCount);
          let end = start + Math.min(maxWindow, all.length - start);

          // Nếu đã đạt maxWindow, chỉ giữ lại đúng maxWindow tin tính từ start
          return {
            rooms: {
              ...state.rooms,
              [topicId]: {
                ...prev,
                visibleMessages: all.slice(start, end)
              }
            }
          };
        }),

      appendMessages: (roomId, count) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          const all = prev.allMessages;
          const messageCount = count || state.messageCount;
          const maxWindow = state.maxWindow;

          // Lấy id message cuối cùng hiện tại
          const lastId = prev.visibleMessages?.[prev.visibleMessages.length - 1]?.id;
          const idx = all.findIndex((m) => m.id === lastId);

          if (idx === -1) return state; // Nếu ko tìm thấy thì không làm gì cả

          // Tính chỉ số bắt đầu mới (append thêm messageCount vào cuối)
          let end = Math.min(all.length, idx + 1 + messageCount);
          let start = Math.max(0, end - Math.min(maxWindow, end));

          // Nếu đã đạt maxWindow, chỉ giữ lại đúng maxWindow tin tính từ start
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                visibleMessages: all.slice(start, end)
              }
            }
          };
        }),

      setVisibleById: (roomId, id) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          const windowSize = state?.messageCount;
          const all = prev.allMessages;
          const idx = all.findIndex((m) => m.tempId === id || m.id === id);

          if (idx === -1) return state;

          // Lấy windowSize tin centered quanh idx
          let start = Math.max(0, idx - Math.floor(windowSize / 2));
          let end = Math.min(all.length, start + windowSize);

          // Nếu gần cuối, lùi start lại cho đủ windowSize
          if (end - start < windowSize && end === all.length) {
            start = Math.max(0, end - windowSize);
          }

          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                visibleMessages: all.slice(start, end)
                // offset: start, // Có thể bỏ offset luôn!
              }
            }
          };
        }),

      addPendingMessage: (roomId, msg) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                pendingMessages: [...prev.pendingMessages, msg]
              }
            }
          };
        }),

      removePendingMessage: (roomId, tempId) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                pendingMessages: prev.pendingMessages.filter((m) => m.tempId !== tempId)
              }
            }
          };
        }),

      focusToMessage: (roomId, id) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                focusMessageId: id || undefined // Nếu không có id thì bỏ focus
              }
            }
          };
        }),

      setLoading: (roomId, val) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                loading: val
              }
            }
          };
        }),
      setScrollPosition: (roomId, position) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                scrollPosition: position
              }
            }
          };
        }),
      setIsAtBottom: (roomId, isAtBottom) =>
        set((state) => {
          const prev = state.rooms[roomId] || defaultRoomState;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...prev,
                isAtBottom
              }
            }
          };
        }),
      updateNewMessage: (newMessage) => {
        set((state) => {
          return {
            newMessage: {
              ...state.newMessage,
              ...newMessage
            }
          };
        });
      },
      removeFileInNewMessage: (uid) => {
        set((state) => {
          return {
            newMessage: {
              ...state.newMessage,
              filePaths: state.newMessage.filePaths?.filter((filePath) => filePath.fileUpload?.uid !== uid)
            }
          };
        });
      },
      addFileInNewMessage: (file: FilePathAdd) => {
        set((state) => {
          return {
            newMessage: {
              ...state.newMessage,
              filePaths: [...(state.newMessage.filePaths || []), file]
            }
          };
        });
      },
      resetNewMessage: () =>
        set({
          newMessage: { content: '' }
        }),
      // #endregion

      // #region Actions - Rooms
      setRooms: (rooms) =>
        set(() => {
          const newRooms: Record<string, RoomChatState> = {};
          rooms.forEach((room) => {
            newRooms[room.id] = { ...defaultRoomState, ...room } as RoomChatState;
          });
          return { rooms: newRooms };
        }),
      addRooms: (rooms) =>
        set((state) => {
          const newRooms = { ...state.rooms };
          rooms.forEach((room) => {
            newRooms[room.roomId] = room;
          });
          return { rooms: newRooms };
        }),
      setRoomInfo: (roomId, roomInfo) => {
        set((state) => ({
          rooms: { ...state.rooms, [roomId]: roomInfo }
        }));
      },
      updateRoomPartial: (roomId, partial) => {
        set((state) => ({
          rooms: {
            ...state.rooms,
            [roomId]: { ...state.rooms[roomId], ...partial }
          }
        }));
      },
      updateUnReadMessages: (roomId, unreadCount) => {
        set((state) => {
          const prevUnreadMessages = state.rooms[roomId]?.countUnReadMessages;
          return {
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...state.rooms[roomId],
                countUnReadMessages: { ...prevUnreadMessages, quantity: prevUnreadMessages?.quantity + unreadCount }
              }
            }
          };
        });
      },
      loadRoomInfo: async (roomId: string) => {
        try {
          // const currentState = get();
          // const existingRoom = currentState.rooms[roomId];

          // ✅ Check if already loaded với detailed info
          // const hasDetailedInfo =
          //   existingRoom &&
          //   existingRoom.topicMembers &&
          //   existingRoom.topicMembers.length > 0 &&
          //   existingRoom.lastLoadedAt &&
          //   Date.now() - existingRoom.lastLoadedAt < 60 * 1000; // 1 minutes cache

          // if (hasDetailedInfo) {
          //   return; // Skip API call
          // }

          const response = await getTopic(roomId);

          if (response.data) {
            set((state) => {
              const currentRoom = state.rooms[roomId];

              // ✅ Smart merge - preserve important data from getTopics
              const mergedRoom = {
                ...currentRoom,
                ...response.data,
                countUnReadMessages: currentRoom?.countUnReadMessages,
                lastestTopicDetail: currentRoom?.lastestTopicDetail,
                lastLoadedAt: Date.now(),
                topicPins: response.data.topicPins ?? currentRoom?.topicPins ?? []
              };

              return {
                rooms: {
                  ...state.rooms,
                  [roomId]: mergedRoom
                }
              };
            });
          }
        } catch (error) {
          console.error('❌ Failed to load room info:', error);
        }
      },
      loadMoreTopics: async () => {
        try {
          const { topicParams, topicsPagination } = get();
          const nextPage = topicsPagination.currentPage + 1;

          const response = await getTopics({
            ...topicParams,
            page: nextPage
          });

          if (response.data?.results) {
            const { results, pageNumber, totalPages } = response.data;

            // Add new topics
            const currentRooms = get().rooms;
            const newRooms = { ...currentRooms };
            results.forEach((topic: ITopic) => {
              newRooms[topic.id] = { ...defaultRoomState, ...topic } as RoomChatState;
            });

            set({
              rooms: newRooms,
              topicsPagination: {
                currentPage: pageNumber || nextPage,
                totalPages: totalPages || 1,
                hasMore: (pageNumber || nextPage) < (totalPages || 1)
              }
            });
          }
        } catch (error) {
          console.error('❌ Failed to load more topics:', error);
        }
      },
      // #endregion

      // #region Actions - Session Management

      restoreFromIndexedDB: async () => {
        try {
          // 1. Restore messages
          const allTopicIds = await getAllTopicIdsFromDB();
          const messagesData: Record<string, ITopicDetail[]> = {};

          for (const topicId of allTopicIds) {
            const messages = await getMessagesFromDB(topicId);
            if (messages?.length > 0) {
              messagesData[topicId] = messages;
            }
          }

          // 2. Restore files
          const allFiles = await getAllFilesFromDB();
          const filesData: Record<string, any> = {};

          for (const file of allFiles) {
            const blobUrl = URL.createObjectURL(file.blob);
            filesData[file.key] = {
              url: blobUrl,
              lastAccessed: file.lastAccessed || Date.now(),
              fileName: file.metadata?.fileName,
              fileSize: file.metadata?.fileSize
            };
          }

          // 3. Update state
          set({
            files: filesData,
            _hasHydrated: true,
            isInitialized: true
          });

          console.log('✅ IndexedDB restore completed:', {
            topics: Object.keys(messagesData).length,
            files: Object.keys(filesData).length
          });
        } catch (error) {
          console.error('❌ IndexedDB restore failed:', error);
          set({ _hasHydrated: true, isInitialized: true });
        }
      },
      // #endregion

      // #region Actions - Topic Params
      updateTopicParams: (updater) =>
        set((state) => {
          const newParams = updater(state.topicParams);

          // Reset pagination nếu thay đổi filter
          const shouldResetPagination =
            newParams.isActive !== state.topicParams.isActive ||
            newParams.searchCondition !== state.topicParams.searchCondition;

          return {
            topicParams: newParams,
            ...(shouldResetPagination && {
              topicsPagination: {
                currentPage: 1,
                totalPages: 1,
                hasMore: true
              }
            })
          };
        }),

      resetTopicParams: () =>
        set({
          topicParams: { page: 1, pageSize: 50, isActive: true },
          topicsPagination: { currentPage: 1, totalPages: 1, hasMore: true }
        })
      // #endregion
      // #endregion
    }),
    {
      name: 'chat-file-store'
    }
  )
);

// ✅ Enhanced auto cleanup
const AUTO_REMOVE_MS = 120 * 60 * 1000; // 120 phút

setInterval(() => {
  const state = useChatStore.getState();

  if (!state._hasHydrated) return; // Wait for hydration

  const { files, removeFile } = state;
  const now = Date.now();

  Object.entries(files).forEach(([key, fileData]) => {
    if (now - fileData.lastAccessed > AUTO_REMOVE_MS) {
      removeFile(key);
    }
  });
}, 300 * 1000); // Check mỗi 5 phút

// ✅ Export hydration status hook
export const useHasHydrated = () => {
  return useChatStore((state) => state._hasHydrated);
};

export const useIsInitialized = () => {
  return useChatStore((state) => state.isInitialized);
};
